
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client, ClientSubscription } from "@/models/Client";
import { useAuth } from "@/contexts/AuthContext";
import { 
  ClientsPaginationState, 
  ClientsPaginationInfo,
  CLIENTS_PAGINATION_CONFIG 
} from "@/constants/clientsPagination";

// Database response from the paginated function
interface DatabaseClientPaginated {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string;
  address: string | null;
  notes: string | null;
  birth_date: string | null;
  gender: string | null;
  weight_kg: number | null;
  height_cm: number | null;
  registration_date: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  payment_status: string | null;
  last_payment_amount: number;
  last_payment_date: string | null;
  subscriptions: any; // JSON field
  total_count: number;
}

export interface ClientsPaginatedResult {
  clients: Client[];
  pagination: ClientsPaginationInfo;
}

export interface ClientsInfiniteResult {
  clients: Client[];
  hasNextPage: boolean;
  totalItems: number;
}

// Legacy hook for backward compatibility - fetches all clients
export const useClients = () => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: ['clients', currentOrganization?.id],
    queryFn: async (): Promise<Client[]> => {
      if (!currentOrganization?.id) {
        console.log('No current organization, returning empty clients list');
        return [];
      }

      // Use the paginated function with a large page size to get all clients
      const { data, error } = await supabase.rpc('get_clients_paginated', {
        p_organization_id: currentOrganization.id,
        p_page: 1,
        p_page_size: 10000, // Large number to get all clients
      });

      if (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      return data.map(transformDatabaseClientToClient);
    },
    enabled: !!currentOrganization?.id
  });
};

// New paginated hook
export const useClientsPaginated = (paginationState: ClientsPaginationState) => {
  const { currentOrganization } = useAuth();

  return useQuery({
    queryKey: [
      'clients-paginated', 
      currentOrganization?.id,
      paginationState.page,
      paginationState.pageSize,
      paginationState.sortField,
      paginationState.sortDirection,
      paginationState.searchTerm,
      paginationState.selectedWorkoutType,
      paginationState.showSoonExpire,
      paginationState.showUnpaidOnly,
      paginationState.showNoSubscriptionsOnly,
    ],
    queryFn: async (): Promise<ClientsPaginatedResult> => {
      if (!currentOrganization?.id) {
        return {
          clients: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paginationState.pageSize,
          }
        };
      }

      const { data, error } = await supabase.rpc('get_clients_paginated', {
        p_organization_id: currentOrganization.id,
        p_page: paginationState.page,
        p_page_size: paginationState.pageSize,
        p_search_term: paginationState.searchTerm || null,
        p_workout_type_id: paginationState.selectedWorkoutType || null,
        p_payment_status: null, // We handle this through show_unpaid_only
        p_show_soon_expire: paginationState.showSoonExpire,
        p_show_unpaid_only: paginationState.showUnpaidOnly,
        p_show_no_subscriptions_only: paginationState.showNoSubscriptionsOnly,
        p_sort_field: paginationState.sortField,
        p_sort_direction: paginationState.sortDirection,
      });

      if (error) {
        console.error('Error fetching paginated clients:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return {
          clients: [],
          pagination: {
            currentPage: paginationState.page,
            totalPages: 0,
            totalItems: 0,
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paginationState.pageSize,
          }
        };
      }

      const totalItems = data[0]?.total_count || 0;
      const totalPages = Math.ceil(totalItems / paginationState.pageSize);

      return {
        clients: data.map(transformDatabaseClientToClient),
        pagination: {
          currentPage: paginationState.page,
          totalPages,
          totalItems,
          hasNextPage: paginationState.page < totalPages,
          hasPreviousPage: paginationState.page > 1,
          pageSize: paginationState.pageSize,
        }
      };
    },
    enabled: !!currentOrganization?.id,
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  });
};

// New infinite scroll hook
export const useClientsInfinite = (filters: Omit<ClientsPaginationState, 'page' | 'pageSize'>) => {
  const { currentOrganization } = useAuth();
  const pageSize = CLIENTS_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [
      'clients-infinite', 
      currentOrganization?.id,
      filters.sortField,
      filters.sortDirection,
      filters.searchTerm,
      filters.selectedWorkoutType,
      filters.showSoonExpire,
      filters.showUnpaidOnly,
      filters.showNoSubscriptionsOnly,
    ],
    queryFn: async ({ pageParam = 1 }): Promise<ClientsInfiniteResult> => {
      if (!currentOrganization?.id) {
        return {
          clients: [],
          hasNextPage: false,
          totalItems: 0,
        };
      }

      const { data, error } = await supabase.rpc('get_clients_paginated', {
        p_organization_id: currentOrganization.id,
        p_page: pageParam as number,
        p_page_size: pageSize,
        p_search_term: filters.searchTerm || null,
        p_workout_type_id: filters.selectedWorkoutType || null,
        p_payment_status: null,
        p_show_soon_expire: filters.showSoonExpire,
        p_show_unpaid_only: filters.showUnpaidOnly,
        p_show_no_subscriptions_only: filters.showNoSubscriptionsOnly,
        p_sort_field: filters.sortField,
        p_sort_direction: filters.sortDirection,
      });

      if (error) {
        console.error('Error fetching infinite clients:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return {
          clients: [],
          hasNextPage: false,
          totalItems: 0,
        };
      }

      const totalItems = data[0]?.total_count || 0;
      const totalPages = Math.ceil(totalItems / pageSize);
      const hasNextPage = (pageParam as number) < totalPages;

      return {
        clients: data.map(transformDatabaseClientToClient),
        hasNextPage,
        totalItems,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasNextPage ? allPages.length + 1 : undefined;
    },
    enabled: !!currentOrganization?.id,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
};

// Helper function to transform database response to Client model
function transformDatabaseClientToClient(dbClient: DatabaseClientPaginated): Client {
  // Parse JSON subscriptions
  const subscriptions = Array.isArray(dbClient.subscriptions) 
    ? dbClient.subscriptions.filter(Boolean).map((sub: any) => ({
        id: sub.id,
        clientId: sub.clientId,
        workoutTypeId: sub.workoutTypeId,
        sessionsPerMonth: sub.sessionsPerMonth,
        currentCycleSessionsUsed: sub.currentCycleSessionsUsed || 0,
        currentCycleStartDate: sub.currentCycleStartDate,
        lastPaymentTriggerDate: sub.lastPaymentTriggerDate,
        startDate: sub.startDate,
        endDate: sub.endDate,
        isActive: sub.isActive,
        organizationId: sub.organizationId,
        createdAt: sub.createdAt,
        updatedAt: sub.updatedAt,
        price: sub.price || 0,
        paymentStatus: (sub.paymentStatus as ClientSubscription['paymentStatus']) || 'unpaid',
        workoutType: sub.workoutType ? {
          id: sub.workoutType.id,
          name: sub.workoutType.name,
          description: sub.workoutType.description,
          color: sub.workoutType.color
        } : undefined
      }))
    : [];

  return {
    id: dbClient.id,
    firstName: dbClient.first_name,
    lastName: dbClient.last_name,
    email: dbClient.email,
    phone: dbClient.phone,
    address: dbClient.address,
    notes: dbClient.notes,
    birthDate: dbClient.birth_date,
    gender: dbClient.gender as 'male' | 'female' | 'other',
    weightKg: dbClient.weight_kg,
    heightCm: dbClient.height_cm,
    registrationDate: dbClient.registration_date,
    isActive: dbClient.is_active,
    organizationId: dbClient.organization_id,
    createdAt: dbClient.created_at,
    updatedAt: dbClient.updated_at,
    createdBy: dbClient.created_by,
    updatedBy: dbClient.updated_by,
    paymentStatus: (dbClient.payment_status as Client['paymentStatus']) || 'unpaid',
    lastPaymentAmount: dbClient.last_payment_amount || 0,
    lastPaymentDate: dbClient.last_payment_date,
    subscriptions,
  };
}
